/**
 * UI event handlers — immediate context updates with no async state transitions.
 * All events here resolve synchronously via assign() and return to the current state.
 */
import { assign } from 'xstate';
import logger from '../../../logger';
import type { AppStateContext } from '../../../types/appState';

export const uiTopLevelOn = {
  SetChromeModelFromChild: {
    actions: assign<AppStateContext, any>((context, event) => {
      if (context.activeFlexLayoutModel === event.activeFlexLayoutModel) {
        logger.debug('SetChromeModelFromChild: Model reference unchanged, skipping update');
        return {};
      }
      logger.info({ event }, 'SetChromeModelFromChild received in applicationReady - model changed');
      return { activeFlexLayoutModel: event.activeFlexLayoutModel };
    }),
  },

  toggleAttachmentsSection: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'toggleAttachmentsSection');
      return { attachmentsVisibility: !context.attachmentsVisibility };
    }),
  },

  changeEditor: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'changeEditor');
      return { editorType: context.editorType === 'html' ? 'wysiwyg' : 'html' };
    }),
  },

  toggleSidebarVisibility: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'toggleSidebarVisibility');
      return { showSidebar: !context.showSidebar };
    }),
  },

  setSidebarVisibility: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'setSidebarVisibility');
      const { value } = event.data || {};
      return { showSidebar: value !== undefined ? value : !context.showSidebar };
    }),
  },

  toggleOpenNotesPanelView: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'toggleOpenNotesPanelView');
      return { showOpenNotesPanel: !context.showOpenNotesPanel };
    }),
  },

  setOpenNotesPanelView: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'setOpenNotesPanelView');
      const { value } = event.data || {};
      return { showOpenNotesPanel: value !== undefined ? value : !context.showOpenNotesPanel };
    }),
  },

  toggleSearchVisibility: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'toggleSearchVisibility');
      return { searchVisibility: !context.searchVisibility };
    }),
  },

  closeSearch: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'closeSearch');
      return { searchVisibility: false, searchResults: [], selectedSearchIndex: 0 };
    }),
  },

  setSearchResults: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'setSearchResults');
      const { results } = event.data || {};
      return { searchResults: results !== undefined ? results : [] };
    }),
  },

  clearSearchResults: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'clearSearchResults');
      return { searchResults: [], selectedSearchIndex: 0 };
    }),
  },

  setSelectedSearchIndex: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'setSelectedSearchIndex');
      const { index } = event.data || {};
      return { selectedSearchIndex: index !== undefined ? index : 0 };
    }),
  },

  selectNextSearchResult: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'selectNextSearchResult');
      const maxIndex = context.searchResults.length > 0 ? context.searchResults.length - 1 : 0;
      return { selectedSearchIndex: Math.min(context.selectedSearchIndex + 1, maxIndex) };
    }),
  },

  selectPreviousSearchResult: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'selectPreviousSearchResult');
      return { selectedSearchIndex: Math.max(context.selectedSearchIndex - 1, 0) };
    }),
  },

  togglePanelsVisibility: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ context, event }, 'togglePanelsVisibility');
      return { panelsVisibility: !context.panelsVisibility };
    }),
  },

  toggleCommandPalette: {
    actions: assign<AppStateContext, any>((context) => ({
      showCommandPalette: !context.showCommandPalette,
    })),
  },

  toggleNoteSearch: {
    actions: assign<AppStateContext, any>((context) => ({
      showNoteSearch: !context.showNoteSearch,
    })),
  },

  closeNoteSearch: {
    actions: assign<AppStateContext, any>(() => ({
      showNoteSearch: false,
    })),
  },

  toggleNotebookModal: {
    actions: assign<AppStateContext, any>((context, event) => {
      logger.info({ event }, 'toggleNotebookModal');
      return { openNotebookModal: !context.openNotebookModal };
    }),
  },
};
